import appSettings from '@/app-settings.json';
import { routes } from '@/app/routes';
import { COMPLAINTS_STATUS } from '@/constants';
import * as dashboardActions from '@/dashboard/dashboard-actions';
import { userUpdateComplaintStatus } from '@/data/data-actions';
import * as dataSelectors from '@/data/data-selectors';
import { AsyncRsGrid } from '@/list/components/async-rs-grid';
import { InFlightValue } from '@/list/components/cells/in-flight-value';
import { ComplaintsRouteParams } from '@/types';
import debounce from 'lodash/debounce';
import difference from 'lodash/difference';
import range from 'lodash/range';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useComplaintsStatus } from '../../utils/useComplaintsStatus';
import {
  ComplaintsColDefs,
  getComplaintsInFlightValues,
} from '../complaints-col-defs';
import { COMPLAINTS_COL_IDS } from '../complaints-constants';
import { ComplaintsPageHeader } from './complaints-page-header';
import { ListedComplaintModal } from './in-list-modal';
import { TComplaintData } from './in-list-modal/types';
import {
  ComplaintListCounts,
  ComplaintsListDownloadParams,
  ComplaintsListGridParams,
} from './types';

const Wrapper = styled.div`
  height: 100%;
  margin-top: 26px;
  margin-left: 38px;
  margin-bottom: 38px;
  margin-right: 38px;
  display: flex;
  flex-direction: column;
`;

const defaultDownloadParams: ComplaintsListDownloadParams = {
  autoConvertFormulas: true,
  fileName: 'complaints.xlsx',
  fontSize: 12,
  sheetName: 'deckard',
};

export function ComplaintsPage(): JSX.Element {
  const dispatch = useDispatch();
  const { complaintId } = useParams<ComplaintsRouteParams>();

  const [gridParams, setGridParams] = useState<
    ComplaintsListGridParams | undefined
  >();
  const [displayedColumns, setDisplayedColumns] = useState<string[]>();
  const [complaintCounts, setComplaintCounts] = useState<ComplaintListCounts>({
    total: 0,
    open: 0,
  });

  const { allStatusDescriptions, complaintStatusLabelFactory } =
    useComplaintsStatus();

  const gridApi = useMemo(() => gridParams?.api, [gridParams]);
  const columnApi = useMemo(() => gridParams?.columnApi, [gridParams]);

  const strComplaintsAvailable = useSelector(
    dataSelectors.strComplaintsAvailable
  );
  const complaintsData = useSelector(dataSelectors.complaintsData);

  const onFilterChangedCB = useCallback(
    ({ api }: Pick<ComplaintsListGridParams, 'api'>) => {
      const totalComplaintsCount = api.getDisplayedRowCount();

      const openComplaintsCount = range(totalComplaintsCount)
        .map((rowIndex) => api.getDisplayedRowAtIndex(rowIndex).data)
        .filter(({ status }) => status === COMPLAINTS_STATUS.OPEN).length;

      setComplaintCounts({
        total: totalComplaintsCount,
        open: openComplaintsCount,
      });
    },
    [setComplaintCounts]
  );

  const onGridReadyCB = useCallback(
    (params: ComplaintsListGridParams) => {
      setGridParams(params);
      onFilterChangedCB(params);
    },
    [onFilterChangedCB]
  );

  const onDownloadClickCB = useCallback(() => {
    if (!gridApi) return;

    const params: ComplaintsListDownloadParams = {
      ...defaultDownloadParams,
      ...(appSettings?.COMPLAINTS_CONFIG?.spreadsheetExportParams || {}),
      processCellCallback: (param: unknown) => {
        // @ts-expect-error Untyped state
        const colDef = param.column.getColDef();

        /**
         * "useValueFormatterForExport" is explicitly tested against "false", since we're providing
         * a custom callback for "processCellCallback" to be used in "exportDataAsExcel" to handle
         * filling in the values per data cell.
         *
         * Without having this check, the resulting spreadsheet will fill the cells with data
         * that have been processed by the "valueFormatter" per column definition.
         */
        const shouldUseValueFormatter =
          colDef.valueFormatter && colDef.useValueFormatterForExport !== false;
        // @ts-expect-error Untyped state
        if (!shouldUseValueFormatter) return param.value;

        const valueFormatterParams = {
          // @ts-expect-error Untyped state
          ...param,
          // @ts-expect-error Untyped state
          data: param.node.data,
          // @ts-expect-error Untyped state
          node: param.node,
          // @ts-expect-error Untyped state
          colDef: param.column.getColDef(),
        };

        return colDef.valueFormatter(valueFormatterParams);
      },
    };
    gridApi.exportDataAsExcel(params);
  }, [gridApi]);

  const onRowClicked = useCallback(
    (e: { data: { complaintId: number } }) => {
      dispatch(dashboardActions.openComplaintById(e.data.complaintId));
    },
    [dispatch]
  );

  const onQuickFilterDebounce = useCallback(
    () =>
      debounce((qfValue: string) => {
        if (gridApi) gridApi.setQuickFilter(qfValue);
      }, 250),
    [gridApi]
  );

  const getColumnFilterValues = useCallback(
    (columnId: string): string[] | undefined =>
      gridApi?.getFilterInstance(columnId)?.valueModel?.allValues,
    [gridApi]
  );

  const filterDropdownOptions = useMemo(() => {
    const complaintTypeItems = getColumnFilterValues(
      COMPLAINTS_COL_IDS.complaintTypes
    );
    // const zoneItems = getColumnFilterValues('zones');
    return {
      statusItems: Object.keys(allStatusDescriptions),
      complaintTypeItems,
    };
  }, [getColumnFilterValues, allStatusDescriptions]);

  const onFilterSelectCB = useCallback(
    (columnId: string) => (rawValues: string[] | null) => {
      if (gridApi) {
        // Shallow-copy rawValues using slice(), since we're pushing more items there later
        const values = rawValues?.length ? rawValues.slice() : null;

        if (values && columnId === COMPLAINTS_COL_IDS.status) {
          // Always display in-flight values if a filter is applied to the Status column
          // That ensures that when the user updates a complaint's status from a filtered grid,
          // the complaint would remain visible until the update API call succeeds
          values.push(
            ...getComplaintsInFlightValues(allStatusDescriptions).map((v) =>
              v.toString()
            )
          );
        }

        gridApi.getFilterInstance(columnId).setModel({ values });
        gridApi.onFilterChanged();
      }
    },
    [gridApi, allStatusDescriptions]
  );

  const setDisplayedColumnsCB = useCallback(() => {
    if (columnApi) {
      const colIds = columnApi
        .getAllDisplayedColumns()
        .map((column) => column.colId);
      setDisplayedColumns(colIds);
    }
  }, [columnApi]);

  /**
   * @param {string[]} checkedColumns Use this to update AgGrid column visibility state.
   * Checked columns should be visible. Unchecked columns should not be visible
   */
  const setAgGridColumnsVisible = useCallback(
    (checkedColumns: string[]) => {
      if (!columnApi) return;

      // getting all configured columns in the grid (visible and hidden)
      const allGridColumns = columnApi
        .getAllDisplayedColumns()
        .map((column) => column.colId);
      // computing hidden columns by subtracting checked/visible columns from all columns
      const hiddenColumns = difference(allGridColumns, checkedColumns);

      /**
       * Making an array of columnIds visible does not hide columns that are not in said array.
       * Therefore, in order to update the grid's visible column state, we explicity
       * tell the grid about the visibility state of all columns.
       */
      columnApi.setColumnsVisible(checkedColumns, true);
      columnApi.setColumnsVisible(hiddenColumns, false);
    },
    [columnApi]
  );

  const onStatusCellValueChange = useCallback(
    ({ rowId, cellValue }: { rowId: number; cellValue: string }) => {
      dispatch(
        userUpdateComplaintStatus({
          complaintId: rowId,
          status: cellValue,
        })
      );
    },
    [dispatch]
  );

  const complaintsColDefFactory = useMemo(
    () =>
      new ComplaintsColDefs(onStatusCellValueChange, {
        allStatusDescriptions,
        complaintStatusLabelFactory,
      }),
    [
      onStatusCellValueChange,
      allStatusDescriptions,
      complaintStatusLabelFactory,
    ]
  );

  const onColumnsReset = useCallback(() => {
    setAgGridColumnsVisible(complaintsColDefFactory.getDefaultVisibleColumns());
  }, [setAgGridColumnsVisible, complaintsColDefFactory]);

  useEffect(setDisplayedColumnsCB, [setDisplayedColumnsCB]);

  const colDefs = useMemo(
    () => complaintsColDefFactory.generateAgGridColDefs(),
    [complaintsColDefFactory]
  );

  const { defaultColDef } = complaintsColDefFactory;

  const gridData = useMemo(
    () => InFlightValue.wrapInFlightRows(complaintsData),
    [complaintsData]
  );

  const complaintData = useMemo(() => {
    if (Array.isArray(gridData) && gridData.length && complaintId) {
      return gridData?.find(
        // @ts-expect-error Untyped state
        (e) => String(e.complaintId) === String(complaintId)
      );
    }

    return null;
  }, [complaintId, gridData]);

  if (!strComplaintsAvailable) return <Redirect to={routes.ROOT} />;

  return (
    <>
      <Wrapper data-testid="complaints-list-view">
        <ComplaintsPageHeader
          totalComplaintsCount={complaintCounts.total}
          openComplaintsCount={complaintCounts.open}
          onDownloadClickCB={onDownloadClickCB}
          onQuickFilterDebounce={onQuickFilterDebounce}
          filterDropdownOptions={filterDropdownOptions}
          onFilterSelectCB={onFilterSelectCB}
          displayedColumns={displayedColumns}
          onColPickerChangeCB={setAgGridColumnsVisible}
          onColumnsReset={onColumnsReset}
        />
        <AsyncRsGrid
          // Prevent the grid from scrolling back to the top when `complaintsData` changes
          immutableData
          // Unique ids are needed by `immutableData` to reconcile updates to `complaintsData`
          // We also need node ids to be complaintIds
          // to handle the change callback from EditableDropdownCell
          getRowNodeId={(complaint: TComplaintData) => complaint.complaintId}
          onGridReady={onGridReadyCB}
          onRowClicked={onRowClicked}
          columnDefs={colDefs}
          rowData={gridData}
          defaultColDef={defaultColDef}
          sideBar={false}
          onColumnVisible={setDisplayedColumnsCB}
          onFilterChanged={onFilterChangedCB}
          onRowDataUpdated={onFilterChangedCB}
        />
      </Wrapper>

      {complaintData && complaintId && (
        <ListedComplaintModal
          currentComplaintId={Number(complaintId)}
          // @ts-expect-error gridData is not typed
          complaintData={complaintData}
          updateComplaintStatus={(complaintId: number, status: string) =>
            dispatch(userUpdateComplaintStatus({ complaintId, status }))
          }
        />
      )}
    </>
  );
}
